import React, { useMemo } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { Flex, Loader } from "@fluentui/react-northstar";

import { EventContext } from "../context/EventContext";
import Account from "../pages/Account";
import Assignment from "../pages/Assignment/Assignment";
import StudentAssignmentHome from "../pages/Assignment/Student/Assignments/AssignmentHome";
import Attendance from "../pages/Attendance/Attendance";
import Events from "../pages/Events";
import DiscoverEvents from "../pages/Events/DiscoverEvents";
import EventFavourites from "../pages/Events/Favourites";
import Registrations from "../pages/Events/Registrations";
import EditEvent from "../pages/Events/edit";
import EventDetails from "../pages/Events/eventDetails";
import NewEvent from "../pages/Events/new";
import Feedback from "../pages/Feedback/Feedback";
import History from "../pages/History/AttendanceHistory";
import { LessonImagePicker } from "../pages/LessonImagePicker";
import Settings from "../pages/Settings/Settings";
import Startup from "../pages/Startup/Startup";
import TabConfig from "../pages/TabConfig";
import Whiteboard from "../pages/Whiteboard/Whiteboard";

import { BookingContext } from "../context/BookingContext";
import { FeedBackContext } from "../context/FeedbackContext";
import { TutorRequestContext } from "../context/TutorRequestContext";
import AiStudio from "../pages/AiStudio";
import Booking from "../pages/Booking";
import ScheduleMeeting from "../pages/Booking/Components/ScheduleMeeting/ScheduleMeeting";
import TutorProfile from "../pages/Booking/Components/TutorProfile/TutorProfile";
import ChartsWrapper from "../pages/Charts";
import { BundleEvents, Bundles as EventBundles } from "../pages/Events/Bundles";
import PageNotFound from "../pages/PageNotFound/PageNotFound";
import Student from "../pages/Student/Student";
import ExamDashboard from "../pages/ExamDashboard";
import { TrustedNetwork } from "../pages/TrustedNetwork";
import { useAiRoutes } from "./useAiRoutes";
import SkillBridge from "../pages/SkillBridge";
const subscription = JSON.parse(localStorage.getItem("subscription"));

const generateRoutes = (
  data,
  role,
  path,
  aiRoutes,
  loading,
  error,
  reportRoutes
) => {
  let redirectToPageNotFound = false;

  // Get the first enabled non-excluded extension
  const excludedTypes = [
    "attendance",
    "assignment",
    "dashboard",
    "whiteBoard",
    "events",
    "booking",
    "feedback",
    "aiInsights",
    "generateAvatar",
    "skillBridge",
    "examDashboard"
  ];

  const enabledExtension = Object.entries(data?.selectedExtension || {}).find(
    ([key, value]) =>
      !excludedTypes.includes(key) && (value === true || value === "true")
  );

  const dynamicExtensionType = enabledExtension ? enabledExtension[0] : null;

  // Don't redirect to page not found if we're still loading
  redirectToPageNotFound = loading
    ? false
    : ((path === "/attendance" || path === "/attendance/*") &&
      !data?.selectedExtension?.attendance) ||
    ((path === "/assignments" || path === "/assignments/*") &&
      !data?.selectedExtension?.assignment) ||
    (path === "/whiteboard" && !data?.selectedExtension?.whiteBoard) ||
    (path === "/dashboard" && !data?.selectedExtension?.dashboard) ||
    ((path === "/feedback" || path === "/feedback/*") &&
      !data?.selectedExtension?.feedback) ||
    ((path === "/events" || path === "/events/*") &&
      !data?.selectedExtension?.booking) ||
    ((path === "/booking" || path === "/booking/*") &&
      !data?.selectedExtension?.booking) ||
    // Dynamic extension check
    (dynamicExtensionType &&
      path === `/${dynamicExtensionType}` &&
      !enabledExtension);

  let redirectToPath = null;

  // Check conditions for redirection
  if (role === "Partner" || role === "Ministry") {
    redirectToPath = "/skillBridge";
  } else if (data?.selectedExtension?.attendance) {
    redirectToPath = "/attendance";
  } else if (data?.selectedExtension?.dashboard) {
    redirectToPath = "/dashboard";
  } else if (data?.selectedExtension?.assignment) {
    redirectToPath = "/assignments";
  } else if (data?.selectedExtension?.whiteBoard) {
    redirectToPath = "/whiteboard";
  } else if (data?.selectedExtension?.feedback) {
    redirectToPath = "/whiteboard";
  } else if (data?.selectedExtension?.events) {
    redirectToPath = "/events";
  } else if (data?.selectedExtension?.booking) {
    redirectToPath = "/booking";
  } else if (dynamicExtensionType) {
    // Dynamic extension redirect
    redirectToPath = `/${dynamicExtensionType}`;
  } else {
    redirectToPath = "/account";
  }

  const commonRoutes = [
    {
      path: "/examDashboard",
      render: (props) => <ExamDashboard {...data} {...props} />,
    },
    {
      path: "/whiteboard",
      render: (props) => <Whiteboard {...data} {...props} />,
    },
    {
      path: "/settings/*",
      render: (props) => <Settings {...data} {...props} />,
    },
    {
      path: "/skillBridge",
      render: (props) => <SkillBridge {...data} {...props} />,
    },
    { path: "/config", render: (props) => <TabConfig {...data} {...props} /> },
    {
      path: "/dashboard",
      render: (props) => <ChartsWrapper {...data} {...props} />,
    },
    // Add dynamic dashboard routes for reportRoutes
    ...(reportRoutes && reportRoutes.length > 0
      ? reportRoutes.map((route) => ({
        path: `/dashboard/${route.path}`,
        render: (props) => (
          <AiStudio
            type={route.type}
            url={route.url}
            config={route.additionalParams || {}}
            {...data}
            {...props}
          />
        ),
      }))
      : loading
        ? [
          {
            // Add a placeholder route when loading is true but reportRoutes is empty
            path: `/dashboard/*`,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ]
        : []),
    {
      path: "/events/discover",
      render: (props) => (
        <EventContext {...data}>
          <DiscoverEvents {...data} {...props} />{" "}
        </EventContext>
      ),
    },
    {
      path: "/events/my-events",
      render: (props) => (
        <EventContext {...data}>
          <Events {...data} {...props} />
        </EventContext>
      ),
    },
    {
      path: "/events/favourites",
      render: (props) => (
        <EventContext {...data}>
          <EventFavourites {...data} {...props} />
        </EventContext>
      ),
    },
    {
      path: "/event/new",
      render: (props) => (
        <EventContext {...data}>
          <NewEvent {...data} {...props} />
        </EventContext>
      ),
    },
    {
      path: "/events/registrations",
      render: (props) => (
        <EventContext {...data}>
          <Registrations {...data} {...props} />
        </EventContext>
      ),
    },
    {
      path: "/event/edit/:id",
      render: (props) => <EditEvent {...data} {...props} />,
    },
    {
      path: "/event/details/:id",
      render: (props) => <EventDetails {...data} {...props} />,
    },
    {
      path: "/events/bundles",
      render: (props) => (
        <EventContext {...data}>
          <EventBundles {...data} {...props} />
        </EventContext>
      ),
    },
    {
      path: "/events/bundles/:bundleId",
      render: (props) => (
        <EventContext {...data}>
          {" "}
          <BundleEvents {...data} {...props} />
        </EventContext>
      ),
    },
    {
      path: "/feedback/*",
      render: (props) => (
        <FeedBackContext {...data}>
          <Feedback {...data} {...props} />
        </FeedBackContext>
      ),
    },
    { path: "/account", render: (props) => <Account {...data} {...props} /> },
    {
      path: "/booking",
      render: (props) => (
        <BookingContext {...data}>
          <Booking {...data} {...props} />
        </BookingContext>
      ),
    },
    {
      path: "/tutor/:mail/:role",
      render: (props) => (
        <BookingContext {...data}>
          <TutorProfile {...data} {...props} />
        </BookingContext>
      ),
    },
    {
      path: "/schedule-meeting/:mail/:role",
      render: (props) => (
        <BookingContext {...data}>
          <ScheduleMeeting {...data} {...props} />
        </BookingContext>
      ),
    },
    {
      path: "/edit-schedule-meeting/:mail/:role/:slotId",
      render: (props) => (
        <BookingContext {...data}>
          <ScheduleMeeting {...data} {...props} />
        </BookingContext>
      ),
    },
    {
      path: "/switch-db",
      render: (props) => <Redirect to="/" />,
    },
    {
      path: "/auth",
      render: (props) => <Redirect to="/" />,
    },
    {
      path: "/",
      render: (props) => <Redirect to={redirectToPath} />,
    },
  ];

  // Modify dynamicAiRoutes to handle empty or invalid responses
  const dynamicAiRoutes = () => {
    // If loading, return a loading route for dynamic AI paths
    if (loading) {
      // Get the first enabled non-excluded extension for path
      const enabledExtension = Object.entries(
        data?.selectedExtension || {}
      ).find(
        ([key, value]) =>
          !excludedTypes.includes(key) && (value === true || value === "true")
      );

      const dynamicExtensionType = enabledExtension
        ? enabledExtension[0]
        : null;

      if (dynamicExtensionType) {
        return [
          {
            path: `/${dynamicExtensionType}`,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
          // Add a wildcard route to catch all paths for this extension during loading
          {
            path: `/${dynamicExtensionType}/*`,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ];
      }
      // Return a fallback loader route for any dynamic path when loading
      return [
        {
          path: `/*`,
          render: (props) => (
            <Flex
              fill
              hAlign="center"
              vAlign="center"
              style={{ height: "calc(-60px + 100vh)" }}
            >
              <Loader size="medium" />
            </Flex>
          ),
        },
      ];
    }

    if (error) {
      console.error("Error loading AI routes:", error);
      return [];
    }

    // Check if aiRoutes exists and is an array
    if (!aiRoutes || !Array.isArray(aiRoutes)) {
      console.warn("AI routes is not an array or is undefined");
      return [];
    }

    // Map routes with null check
    const routes = aiRoutes
      .map((route) => {
        if (!route || !route.path || !route.type || !route.url) {
          console.warn("Invalid route configuration:", route);
          return null;
        }

        return {
          path: `/${route.path}`,
          render: (props) => {
            try {
              return (
                <AiStudio
                  type={route.type}
                  url={route.url}
                  config={route.additionalParams || {}}
                  {...data}
                  {...props}
                />
              );
            } catch (error) {
              console.error("Error rendering AI Studio:", error);
              return <div>Error loading AI component</div>;
            }
          },
        };
      })
      .filter(Boolean); // Remove any null routes

    return routes;
  };

  // Safely combine routes
  const routesWithAi = [
    ...commonRoutes,
    ...(Array.isArray(dynamicAiRoutes()) ? dynamicAiRoutes() : []),
  ];

  const teacherRoute = [
    ...routesWithAi,
    {
      path: "/assignments",
      render: (props) => <Assignment {...data} {...props} />,
    },
    {
      path: "/attendance",
      render: (props) => <Attendance {...data} {...props} />,
    },
    { path: "/history", render: (props) => <History {...data} {...props} /> },
  ];

  const superAdminRoute = [
    ...teacherRoute,
    {
      path: "/settings",
      render: (props) => <Settings {...data} {...props} />,
    },
    {
      path: "/start-up",
      render: (props) => {
        if (
          data.user.slug === "?slug=acvpreprod" ||
          data.user.slug === "?slug=acvapp"
        ) {
          return <Redirect to="/page-not-found" />;
        }
        return <Startup {...data} {...props} />;
      },
    },
    {
      path: "/start-up/*",
      render: (props) => {
        if (
          data.user.slug === "?slug=acvpreprod" ||
          data.user.slug === "?slug=acvapp"
        ) {
          return <Redirect to="/page-not-found" />;
        }
        return <Startup {...data} {...props} />;
      },
    },
    {
      path: "/lesson-image-picker",
      render: (props) => <LessonImagePicker {...data} {...props} />,
    },
    {
      path: "/trusted-network",
      render: (props) => {
        if (!data?.selectedExtension?.whiteBoard) {
          return <Redirect to="/page-not-found" />;
        }
        return <TrustedNetwork {...data} {...props} />;
      },
    },
  ];

  const studentRoute = [
    {
      path: "/assignments",
      exact: false,
      render: (props) => <StudentAssignmentHome {...data} {...props} />,
    },
    {
      path: "/attendance",
      render: (props) => (
        <Redirect
          {...data}
          {...props}
          to={
            data?.selectedExtension?.assignment
              ? "/assignments"
              : data?.selectedExtension?.whiteBoard
                ? "/whiteboard"
                : data?.selectedExtension?.feedback
                  ? "/feedback/submit-feedback"
                  : data?.selectedExtension?.events
                    ? "/events/my-events"
                    : "/account"
          }
        />
      ),
    },
  ];

  const parentRoutes = [
    {
      path: "/parent/student",
      render: (props) => <Student {...data} {...props} />,
    },
    {
      path: "/feedback/*",
      render: (props) => (
        <FeedBackContext {...data}>
          <Feedback {...data} {...props} />
        </FeedBackContext>
      ),
    },
    {
      path: "/attendance",
      render: (props) => <Redirect to="/parent/student" />,
    },
    { path: "/account", render: (props) => <Account {...data} {...props} /> },
    {
      path: "/auth",
      render: (props) => <Redirect to="/parent/student" />,
    },
    {
      path: "/",
      render: (props) => <Redirect to="/parent/student" />,
    },
    {
      path: "/events/discover",
      render: (props) => (
        <EventContext {...data}>
          <DiscoverEvents {...data} {...props} />{" "}
        </EventContext>
      ),
    },
    {
      path: "/event/details/:id",
      render: (props) => <EventDetails {...data} {...props} />,
    },
    {
      path: "/events/registrations",
      render: (props) => (
        <EventContext {...data}>
          <Registrations {...data} {...props} />
        </EventContext>
      ),
    },
  ];

  const externalTutorRoutes = [
    { path: "/account", render: (props) => <Account {...data} {...props} /> },
    {
      path: "/booking",
      render: (props) => (
        <BookingContext {...data}>
          <Booking {...data} {...props} />
        </BookingContext>
      ),
    },
    {
      path: "/tutor/:mail/:role",
      render: (props) => (
        <BookingContext {...data}>
          <TutorProfile {...data} {...props} />
        </BookingContext>
      ),
    },
    {
      path: "/auth",
      render: (props) => <Redirect to="/booking" />,
    },
    {
      path: "/",
      render: (props) => <Redirect to="/booking" />,
    },
  ];

  const parentPlusExternalTutorRoutes = [
    ...parentRoutes,
    ...externalTutorRoutes,
  ];

  // Add a catch-all route for 404 at the end of each role's routes
  const add404Route = (routes) => [
    ...routes,
    {
      path: "*", // This catches all unmatched routes
      exact: false,
      render: (props) => <PageNotFound {...data} {...props} />,
    },
  ];

  switch (role) {
    case "Student":
      return loading
        ? // Show loader during loading state
        [
          {
            path: "*",
            exact: false,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ]
        : redirectToPageNotFound
          ? [
            {
              path: "*",
              exact: false,
              render: (props) => <PageNotFound {...data} {...props} />,
            },
          ]
          : add404Route([...routesWithAi, ...studentRoute]);
    case "Teacher":
      return loading
        ? // Show loader during loading state
        [
          {
            path: "*",
            exact: false,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ]
        : redirectToPageNotFound
          ? [
            {
              path: "*",
              exact: false,
              render: (props) => <PageNotFound {...data} {...props} />,
            },
          ]
          : add404Route([...teacherRoute]);
    case "Parent":
      return loading
        ? // Show loader during loading state
        [
          {
            path: "*",
            exact: false,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ]
        : redirectToPageNotFound
          ? [
            {
              path: "*",
              exact: false,
              render: (props) => <PageNotFound {...data} {...props} />,
            },
          ]
          : data.user.isTutor
            ? add404Route([...parentPlusExternalTutorRoutes])
            : add404Route([...parentRoutes]);
    case "ExternalTutor":
      return loading
        ? // Show loader during loading state
        [
          {
            path: "*",
            exact: false,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ]
        : redirectToPageNotFound
          ? [
            {
              path: "*",
              exact: false,
              render: (props) => <PageNotFound {...data} {...props} />,
            },
          ]
          : add404Route([...externalTutorRoutes]);
    default:
      return loading
        ? // Show loader during loading state
        [
          {
            path: "*",
            exact: false,
            render: (props) => (
              <Flex
                fill
                hAlign="center"
                vAlign="center"
                style={{ height: "calc(-60px + 100vh)" }}
              >
                <Loader size="medium" />
              </Flex>
            ),
          },
        ]
        : redirectToPageNotFound
          ? [
            {
              path: "*",
              exact: false,
              render: (props) => <PageNotFound {...data} {...props} />,
            },
          ]
          : add404Route([...superAdminRoute]);
  }
};

const PageRouter = (propsData) => {
  const role = propsData?.user?.role;
  const path = window.location.pathname;
  const history = useHistory();
  const { aiRoutes, loading, error, reportRoutes } = useAiRoutes(
    propsData?.selectedExtension
  );

  const routes = useMemo(() => {
    try {
      const _routes = generateRoutes(
        propsData,
        role,
        path,
        aiRoutes,
        loading,
        error,
        reportRoutes
      );

      return _routes || [];
    } catch (error) {
      console.error("Error generating routes:", error);
      // Return a 404 route if there's an error
      return [
        {
          path: "*",
          exact: false,
          render: (props) => <PageNotFound {...propsData} {...props} />,
        },
      ];
    }
  }, [propsData, role, path, aiRoutes, loading, error, reportRoutes]);

  return (
    <TutorRequestContext {...propsData}>
      <Switch>
        {routes.map((route, index) => {
          const { exact = true } = route;

          return (
            <Route
              key={route.path}
              path={route.path}
              exact={exact}
              render={(props) => route.render(props)}
            />
          )
        })}
      </Switch>
    </TutorRequestContext>
  );
};

export default PageRouter;
